import axios, { AxiosInstance } from 'axios';
import { ITenant } from 'interfaces/tenants';
import { ResponseError } from 'interfaces/api-error';
import { Tracker } from 'services/tracker';
import {
  clearSession,
  getRefreshToken,
  getToken,
  setSession
} from 'services/auth';
import { getTenant } from 'services/tenants';
import { refresh } from 'requests/auth';

const RETRY_LIMIT = 0;
const UNAUTHORIZED = 401;

const apiMap = new Map<string, AxiosInstance>();
const refreshingMap = new Map<string, Promise<void> | null>();

export const getAPI = () => {
  const tenant = getTenant();

  const api = apiMap.get(tenant.name) || generateAPI(tenant);
  apiMap.set(tenant.name, api);

  return api;
};

const clear = () => {
  clearSession();
  window.location.reload();
};

const handleAdminErrorTriggered = (error: ResponseError, status: number) => {
  Tracker.adminErrorTriggered({
    error_code: error.code || status.toString(),
    error_message: error.message || 'Unknown error',
    screen: window.location.href
  });
};

const generateAPI = (tenant: ITenant) => {
  const api = axios.create({
    baseURL: tenant.apiUrl
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  api.interceptors.request.use((config: any) => {
    let { headers } = config;
    const token = getToken();
    const retryCount = config.retryCount || 0;

    headers = headers || {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return {
      ...config,
      headers,
      retryCount
    };
  });

  api.interceptors.response.use(
    response => response,
    async error => {
      const { response, config, message: axiosMessage } = error;
      const retryCount = config?.retryCount ?? 0;

      if (!response) {
        const networkError = {
          ...config,
          message: axiosMessage ?? 'Erro de rede',
          status: 0,
          statusCode: 0
        };

        handleAdminErrorTriggered(networkError as ResponseError, 0);

        throw networkError;
      }

      const data = response.data as Record<string, unknown> | undefined;
      const messageFromBody =
        data && typeof data.message === 'string' && data.message.length > 0
          ? data.message
          : undefined;

      const customError: ResponseError & { statusCode: number } = {
        ...config,
        ...(typeof data === 'object' && data !== null ? data : {}),
        message: messageFromBody ?? axiosMessage ?? 'Erro na requisição',
        status: response.status,
        statusCode: response.status
      };

      if (response.status !== UNAUTHORIZED) {
        handleAdminErrorTriggered(customError, response.status);

        throw customError;
      }

      if (retryCount > RETRY_LIMIT) {
        handleAdminErrorTriggered(customError, response.status);

        throw customError;
      }

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clear();
        throw customError;
      }

      refreshingMap.get(tenant.name) ||
        refreshingMap.set(
          tenant.name,
          refresh({ refreshToken })
            .then(session => {
              setSession(session);
            })
            .finally(() => {
              refreshingMap.set(tenant.name, null);
            })
        );

      const retryConfig = {
        ...config,
        retryCount: retryCount + 1
      };

      try {
        await refreshingMap.get(tenant.name);
        return api(retryConfig);
      } catch {
        clear();
        throw new Error(String(error));
      }
    }
  );

  return api;
};
